import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function CartPreview() {
  const { items, removeFromCart, getTotal, itemCount } = useCart();
  const [isOpen, setIsOpen] = useState(false);

  const subtotal = getTotal();
  const shipping = items.reduce((sum, item) => sum + (item.shipping_rate || 15.0), 0);
  const total = subtotal + shipping;

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        data-testid="cart-preview-btn"
      >
        <ShoppingCart className="h-5 w-5" />
        {itemCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {itemCount}
          </span>
        )}
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Cart Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-lg shadow-lg z-50"
              data-testid="cart-preview-dropdown"
            >
              <div className="p-4 border-b border-border flex justify-between items-center">
                <h3 className="font-serif text-lg">Shopping Cart</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="hover:text-accent transition-colors"
                  data-testid="close-cart-preview"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    Your cart is empty
                  </div>
                ) : (
                  <div className="p-4 space-y-4">
                    {items.map((item) => (
                      <div
                        key={item.artwork_id}
                        className="flex gap-3 group"
                        data-testid={`cart-preview-item-${item.artwork_id}`}
                      >
                        <Link
                          to={`/artwork/${item.artwork_id}`}
                          onClick={() => setIsOpen(false)}
                          className="flex-shrink-0"
                        >
                          <img
                            src={item.artwork_image}
                            alt={item.artwork_title}
                            className="w-16 h-20 object-cover rounded"
                          />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/artwork/${item.artwork_id}`}
                            onClick={() => setIsOpen(false)}
                            className="hover:text-accent transition-colors"
                          >
                            <p className="font-medium text-sm truncate">
                              {item.artwork_title}
                            </p>
                          </Link>
                          <p className="text-sm text-muted-foreground">
                            ${item.price.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            + ${(item.shipping_rate || 15.0).toFixed(2)} shipping
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.artwork_id)}
                          className="text-muted-foreground hover:text-destructive transition-colors self-start"
                          data-testid={`remove-preview-item-${item.artwork_id}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {items.length > 0 && (
                <div className="p-4 border-t border-border space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span data-testid="preview-subtotal">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shipping</span>
                      <span data-testid="preview-shipping">${shipping.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-medium text-base pt-2 border-t border-border">
                      <span>Total</span>
                      <span data-testid="preview-total">${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Link to="/cart" className="flex-1" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full" data-testid="view-cart-btn">
                        VIEW CART
                      </Button>
                    </Link>
                    <Link to="/checkout" className="flex-1" onClick={() => setIsOpen(false)}>
                      <Button className="w-full rounded-full" data-testid="checkout-preview-btn">
                        CHECKOUT
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
